import { useEffect, useState } from "react";
import * as styles from "./CTA.module.css";
import successVideo from "url:../../assets/webm/success.webm";
import errorVideo from "url:../../assets/webm/failed.webm";
import arrowIcon from "../../assets/svg/arrow.svg";
import unlock from "../../assets/images/unlock.png";

interface CTAProps {
  variant: "large" | "small";
  status: "idle" | "success" | "error";
  setStatus: (status: "idle" | "success" | "error") => void;
}

const CTA = ({ variant, status, setStatus }: CTAProps) => {
  const [phone, setPhone] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0 });
  const [placeholder, setPlaceholder] = useState("Enter phone number");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  const API_URL = "https://tradeinsights.co.in/v1/api/users/contact";
  const CONTACT_DETAILS = {
    large: { name: "Home Page Landing Area", email: "contactuser@gmail.com" },
    small: { name: "Footer", email: "contactuser@gmail.com" },
  };

  const formatPhoneNumber = (value: string) => {
    let formatted = value.replace(/\D/g, "").slice(0, 10);
    if (formatted.length > 5) {
      formatted = formatted.replace(/^(\d{5})(\d{0,5})$/, "$1$2");
    }
    return formatted;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const formattedValue = formatPhoneNumber(rawValue);
    setPhone(formattedValue);
    const plainNumber = formattedValue.replace(/\s/g, "");
    setIsValid(/^[6-9]\d{9}$/.test(plainNumber));
  };

  const handleRewardClick = async () => {
    if (!isValid || isLoading) return;

    setIsLoading(true);
    setStatus("idle");
    setTimeout(() => {
      setStatus("success");
      handleCloseModal();
      setIsLoading(false);
    }, 500);
  
    const { name, email } = CONTACT_DETAILS[variant];

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          message: phone.replace(/\s/g, ""),
        }),
      });

      if (!response.ok) throw new Error("Failed to send contact request");

      setStatus("success");
      handleCloseModal();
    } catch (error) {
      console.error(error);
      setStatus("error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLSpanElement>) => {
    if (!isValid) {
      setTooltip({ visible: true, x: e.clientX, y: e.clientY + 40 });
    }
  };

  const handleMouseLeave = () => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  };

  useEffect(() => {
    const updatePlaceholder = () => {
      setPlaceholder(
        window.innerWidth <= 600 ? "Phone number" : "Enter phone number"
      );
    };

    updatePlaceholder();
    window.addEventListener("resize", updatePlaceholder);

    return () => window.removeEventListener("resize", updatePlaceholder);
  }, []);

  const handleInputClick = () => {
    // if (variant === "large" && window.innerWidth <= 600) {
    //   setIsModalOpen(true);
    // }
  };

  const handleCloseModal = () => {
    setIsFadingOut(true);
    setTimeout(() => {
      setIsModalOpen(false);
      setIsFadingOut(false);
    }, 300);
  };

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isModalOpen]);

  useEffect(() => {
    const handleResize = () => {
      const innerHeight = window.innerHeight;
      const clientHeight = document.documentElement.clientHeight;
      if (innerHeight < clientHeight) {
        setKeyboardHeight(clientHeight - innerHeight);
      } else {
        setKeyboardHeight(0);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className={styles.ctaContainer}>
      {status === "success" && variant != "small" ? (
        <div
          className={`${styles.ctaParent} ${styles[variant]} ctaParentDownloadParent`}
        >
          <button
            className={styles.ctaParentDownload}
            onClick={() => {
              const userAgent = navigator.userAgent || navigator.vendor;
              if (/android/i.test(userAgent)) {
                window.location.href =
                  "https://play.google.com/store/apps/details?id=com.optionbase.optionbase&hl=ln";
              } else if (
                /iPad|iPhone|iPod/.test(userAgent) ||
                /Macintosh/.test(userAgent)
              ) {
                window.location.href =
                  "https://apps.apple.com/in/app/returns-stocks-investments/id6736361692";
              } else {
                window.location.href =
                  "https://apps.apple.com/in/app/returns-stocks-investments/id6736361692"; // Default to App Store
              }
            }}
          >
            Download Returns App
            <img
              src={arrowIcon}
              alt="Arrow Icon"
              style={{ width: "14px", height: "14px", fontWeight: "400" }}
            />
          </button>
        </div>
      ) : (
        <div className={`${styles.ctaParent} ${styles[variant]}`}>
          <span className={styles.contactParent}>
            <span className={styles.countryCode}>+91</span>
            <span className={styles.contactWrapper}>
              <input
                className={styles.contactInput}
                type="tel"
                value={phone}
                onChange={handleChange}
                placeholder={placeholder}
                onClick={handleInputClick}
                inputMode="numeric"
              />
            </span>
          </span>

          <button
            className={`${styles.reward} ${
              !isValid ? styles.disabledReward : ""
            }`}
            onClick={handleRewardClick}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            {variant === "large" && (
              <>
                Claim 3 <span className={styles.free}>free</span> stocks
              </>
            )}
            <span className={styles.rewardSvg}>
              {isLoading ? (
                <span className={styles.loader}></span>
              ) : status === "error" ? (
                <video
                  key={status}
                  src={`${errorVideo}?t=${Date.now()}`}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className={styles.errorAnim}
                />
              ) : (
                <img
                  src={arrowIcon}
                  alt="Arrow Icon"
                  style={{ width: "14px", height: "14px", fontWeight: "400" }}
                />
              )}
            </span>
          </button>

          {tooltip.visible && (
            <div
              className={styles.tooltip}
              style={{ left: `${tooltip.x}px`, top: `${tooltip.y}px` }}
            >
              Enter valid phone number
            </div>
          )}
        </div>
      )}
      {isModalOpen && (
        <div
          className={`${styles.modalOverlay} ${
            isFadingOut ? styles.fadeOut : styles.fadeIn
          }`}
          onClick={handleCloseModal}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <button className={styles.closeButton} onClick={handleCloseModal}>
              ✕
            </button>
            <div className={styles.modalContentMain}
            style={{
              position: keyboardHeight ? 'absolute' : 'relative',
              bottom: keyboardHeight ? `${keyboardHeight + 10}px` : 'auto'
            }}>
              <span className={styles.modalHeadingParent}>
                <h1 className={styles.modalHeading}>
                  Get 3 free Stocks worth ₹300
                </h1>
              </span>
              <img src={unlock} alt="Background" className={styles.modalBg} />
              <div className={`${styles.ctaParent} ${styles[variant]}`}>
                <span className={styles.contactParent}>
                  <span className={styles.countryCode}>+91</span>
                  <span className={styles.contactWrapper}>
                    <input
                      className={styles.contactInput}
                      type="tel"
                      value={phone}
                      onChange={handleChange}
                      placeholder={placeholder}
                      autoFocus
                      inputMode="numeric"
                    />
                  </span>
                </span>
                <button
                  className={`${styles.reward} ${
                    !isValid ? styles.disabledReward : ""
                  }`}
                  onClick={() => {
                    handleRewardClick();
                    const userAgent = navigator.userAgent || navigator.vendor;
                    if (/android/i.test(userAgent)) {
                      window.location.href =
                        "https://play.google.com/store/apps/details?id=com.optionbase.optionbase&hl=ln";
                    } else if (/iPad|iPhone|iPod/.test(userAgent) || /Macintosh/.test(userAgent)) {
                      window.location.href =
                        "https://apps.apple.com/in/app/returns-stocks-investments/id6736361692";
                    } else {
                      window.location.href =
                        "https://apps.apple.com/in/app/returns-stocks-investments/id6736361692";
                    }
                  }}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                >
                  {variant === "large" && (
                    <>
                      Claim 3 <span className={styles.free}>free</span> stocks
                    </>
                  )}
                  <span className={styles.rewardSvg}>
                    {isLoading ? (
                      <span className={styles.loader}></span>
                    ) : status === "error" ? (
                      <video
                        key={status}
                        src={`${errorVideo}?t=${Date.now()}`}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className={styles.errorAnim}
                      />
                    ) : (
                      <img
                        src={arrowIcon}
                        alt="Arrow Icon"
                        style={{
                          width: "14px",
                          height: "14px",
                          fontWeight: "400",
                        }}
                      />
                    )}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { CTA };
